/**
 * Role-based access control helpers
 */

export enum UserRole {
  SUPER_ADMIN = 'SuperAdmin',
  ADMINISTRADOR = 'Administrador',
  GERENTE = 'Gerente',
  MESERO = 'Mesero',
  CHEF = 'Chef',
  CAJERO = 'Cajero',
  CAJERO_MESERO = 'Cajero/Mesero',
}

/**
 * Check if user has access to Dashboard
 */
export const canAccessDashboard = (role?: string): boolean => {
  if (!role) return false;
  return [UserRole.SUPER_ADMIN, UserRole.ADMINISTRADOR, UserRole.GERENTE].includes(role as UserRole);
};

/**
 * Check if user is an admin (SuperAdmin or Administrador)
 */
export const isAdmin = (role?: string): boolean => {
  if (!role) return false;
  return [UserRole.SUPER_ADMIN, UserRole.ADMINISTRADOR].includes(role as UserRole);
};

/**
 * Check if user is a manager (Gerente)
 */
export const isManager = (role?: string): boolean => {
  return role === UserRole.GERENTE;
};

/**
 * ¿Puede GESTIONAR el módulo de Finanzas, y no sólo capturar en él?
 *
 * El Gerente captura gastos y adjunta comprobantes, pero no cancela gastos, no
 * borra comprobantes, no administra el catálogo de métodos de pago, no paga desde
 * caja y no ve el Estado de Resultados. Ver docs/finanzas-module.md.
 *
 * Existe como un solo helper — y no como siete condiciones sueltas — porque la
 * regla se aplica en siete puntos de UI repartidos en cinco archivos, y el bloque
 * de "Agregar nuevo método" está duplicado entre el form de creación y el de
 * edición. Con un helper, el próximo botón que alguien agregue hereda la regla en
 * vez de olvidarla.
 *
 * IMPORTANTE: esconder un botón NO es la barrera de seguridad. El gate real vive
 * en el [Authorize] de cada endpoint; esto sólo evita que el usuario se tope con
 * un 403 al hacer clic.
 */
export const canManageFinanzas = (role?: string, isSupport = false): boolean => {
  return isSupport || isAdmin(role);
};

/**
 * ¿Puede operar CFDI de UNA orden (generar, timbrar, regenerar)? Espejo de
 * FacturacionRoles.Operate en el backend. El Mesero no.
 *
 * IMPORTANTE: esconder el botón no es la barrera; el [Authorize] del endpoint sí.
 */
export const canOperateFacturas = (role?: string, isSupport = false): boolean => {
  if (isSupport) return true;
  return ['SuperAdmin', 'Admin', 'Administrador', 'Gerente', 'Cajero', 'Cajero/Mesero'].includes(role ?? '');
};

/**
 * ¿Puede hacer lo irreversible o lo que abarca muchas órdenes: cancelar ante el SAT,
 * facturación masiva/global y timbrado masivo? Espejo de FacturacionRoles.Manage.
 */
export const canManageFacturas = (role?: string, isSupport = false): boolean => {
  if (isSupport) return true;
  return ['SuperAdmin', 'Admin', 'Administrador'].includes(role ?? '');
};

/**
 * ¿Puede REGISTRAR un gasto? Es el gate de entrada al módulo, no el de alcance.
 *
 * ESPEJO de FinanzasRoles.AnyCapture en el backend. Incluye al personal de piso y caja
 * (Mesero, Cajero, Cajero/Mesero, Chef) desde que el cliente pidió que capturaran sus
 * gastos. Lo que ven una vez adentro lo decide `canSeeAllExpenses`.
 */
export const canRegisterExpenses = (role?: string, isSupport = false): boolean => {
  if (isSupport) return true;
  if (!role) return false;
  return [
    UserRole.SUPER_ADMIN,
    UserRole.ADMINISTRADOR,
    UserRole.GERENTE,
    UserRole.MESERO,
    UserRole.CAJERO,
    UserRole.CAJERO_MESERO,
    UserRole.CHEF,
  ].includes(role as UserRole);
};

/**
 * ¿Puede marcar un gasto como "pagado desde caja"?
 *
 * ESPEJO de FinanzasRoles.CashCapable. Es un eje DISTINTO de `canRegisterExpenses`, no
 * un nivel más: capturar anota, pagar desde caja mueve dinero e inserta un CashMovement
 * que cambia el efectivo esperado del arqueo.
 *
 * Se expresa como "los que cobran, menos el Gerente" porque eso es literalmente la regla:
 * `canProcessPayments` ya excluye a Mesero puro y Chef (no tocan dinero), y el Gerente se
 * excluye aparte por un motivo propio — sí cobra, pero no tiene caja propia, así que
 * cualquiera contra la que cargara sería la de otro.
 */
export const canPayExpenseFromCash = (role?: string, isSupport = false): boolean => {
  if (isSupport) return true;
  return canProcessPayments(role) && !isManager(role);
};

/**
 * ¿Ve TODOS los gastos del restaurante, o sólo los suyos?
 *
 * ESPEJO POR NEGACIÓN de ExpensesController.IsOwnScopeOnly. El backend ya recorta las
 * filas y omite los totales, así que esto no es la barrera — es lo que evita pintar una
 * pantalla de KPIs vacíos, un botón de exportar que exporta cuatro renglones y filtros
 * ("pagado desde caja", "creado por") que no significan nada cuando todo es tuyo.
 *
 * Se define por negación igual que en el servidor: un rol nuevo nace viendo sólo lo suyo.
 */
export const canSeeAllExpenses = (role?: string, isSupport = false): boolean => {
  if (isSupport) return true;
  if (!role) return false;
  // Se define contra `isFloorStaff` y no repitiendo los cuatro roles: eran dos
  // listas idénticas en este mismo archivo, y un rol de piso nuevo agregado a
  // una y no a la otra daba chrome de piso con visibilidad total de gastos.
  // Sigue siendo espejo por negación de ExpensesController.IsOwnScopeOnly, así
  // que tocar `isFloorStaff` obliga a revisar el backend.
  return !isFloorStaff(role);
};

/**
 * Check if user is a waiter (Mesero)
 */
export const isWaiter = (role?: string): boolean => {
  return role === UserRole.MESERO;
};

/**
 * Check if user is a cashier (Cajero)
 */
export const isCashier = (role?: string): boolean => {
  return role === UserRole.CAJERO;
};

/**
 * Check if user is a chef
 */
export const isChef = (role?: string): boolean => {
  return role === UserRole.CHEF;
};

/**
 * Check if user is a cashier/waiter (Cajero/Mesero)
 */
export const isCashierWaiter = (role?: string): boolean => {
  return role === UserRole.CAJERO_MESERO;
};

/**
 * Check if user has cashier access (Admin, Gerente, Cajero, Cajero/Mesero, or Mesero)
 */
export const hasCashierAccess = (role?: string): boolean => {
  if (!role) return false;
  const normalizedRole = role.toLowerCase();
  return normalizedRole === 'cajero'
      || normalizedRole === 'cajero/mesero'
      || normalizedRole === 'mesero'
      || normalizedRole === 'administrador'
      || normalizedRole === 'admin'
      || normalizedRole === 'administrator'
      || normalizedRole === 'gerente'
      || normalizedRole === 'superadmin';
};

/**
 * Puede cobrar: procesar pagos, cobrar cuentas divididas y cerrar la mesa por
 * pago. ESPEJO EXACTO de PaymentFlowRules.IsCashierRole en el backend — el gate
 * real vive allá y mostrar el botón a un rol que el backend rechaza es un 403
 * disfrazado de botón (le pasaba a Gerente en el menú de mesa). El único
 * excluido es Mesero puro: comanda pero no toca dinero.
 */
export const canProcessPayments = (role?: string): boolean => {
  if (!role) return false;
  const normalizedRole = role.toLowerCase();
  return normalizedRole === 'cajero'
      || normalizedRole === 'cajero/mesero'
      || normalizedRole === 'gerente'
      || normalizedRole === 'administrador'
      || normalizedRole === 'admin'
      || normalizedRole === 'administrator'
      || normalizedRole === 'superadmin';
};

/**
 * Puede aplicar y quitar DESCUENTOS de orden. ESPEJO EXACTO de
 * OrdersController.DiscountRoles — el gate real vive allá; esto solo evita ofrecer
 * una acción que el backend contesta con 403.
 *
 * Misma lista que `canProcessPayments` y no por casualidad: bajar el total es parte de
 * decidir cuánto dinero entra, así que descontar acompaña a cobrar. Se declara como
 * helper propio —y no reusando `canProcessPayments` en el sitio de uso— porque son ejes
 * distintos: si mañana el cliente pide que solo Gerente autorice descuentos, se aprieta
 * aquí sin tocar quién cobra.
 *
 * Hasta ahora no había gate NINGUNO (ni aquí ni en el backend) y el único freno era el
 * campo libre "autorizado por" del modal, que teclea el propio usuario: un Mesero podía
 * dejar una mesa en $0 firmándose a sí mismo.
 */
export const canApplyDiscounts = (role?: string): boolean => canProcessPayments(role);

/**
 * Puede cancelar una orden COMPLETA. ESPEJO EXACTO del [Authorize(Roles=...)]
 * de OrdersController.CancelOrder — el gate real vive allá; esto solo evita
 * enseñar un botón que el backend contestaría con 403.
 *
 * Más estrecho que canProcessPayments a propósito: cancelar saca una orden del
 * piso sin cobrarla, así que el Cajero queda fuera aunque sí pueda cobrar.
 */
export const canCancelOrders = (role?: string): boolean => {
  if (!role) return false;
  const normalizedRole = role.toLowerCase();
  return normalizedRole === 'gerente'
      || normalizedRole === 'administrador'
      || normalizedRole === 'admin'
      || normalizedRole === 'administrator'
      || normalizedRole === 'superadmin';
};

/**
 * Puede cancelar RENGLONES de una orden. ESPEJO EXACTO del [Authorize(Roles=…)] de
 * OrdersController.CancelOrderItem / CancelOrderItems.
 *
 * Es la misma lista que `canCancelOrders` y no por casualidad: sacar producto de una
 * cuenta sin cobrarlo tiene el mismo efecto económico renglón a renglón que de golpe.
 * Antes el backend no tenía gate aquí, así que un mesero podía dejar una mesa en $0
 * cancelando uno por uno, firmándose a sí mismo como autorizador — mientras cancelar la
 * orden entera le daba 403.
 */
export const canCancelOrderItems = (role?: string): boolean => canCancelOrders(role);

/* ─── Cash-register access rules ───────────────────────────────────────────
 * Single source of truth: blind counting depends on these. Before this they
 * were hand-rolled (and inconsistent) in CashRegisterManagement vs Card.
 */

/** Blind counting: these roles must NOT see running totals in the cash register UI. */
export const shouldHideCashTotals = (role?: string): boolean =>
  isWaiter(role) || isCashierWaiter(role);

/** Movements list is visible to everyone operating a register except plain waiters. */
export const canSeeCashMovements = (role?: string): boolean => !isWaiter(role);

/** Closing the register (with count or quick) — not for waiter-type roles. */
export const canCloseCashRegister = (role?: string): boolean =>
  !isWaiter(role) && !isCashierWaiter(role);

/** Arqueo (cash count audit) — everyone except Cajero/Mesero. */
export const canDoCashCount = (role?: string): boolean => !isCashierWaiter(role);

/** Cierre de turno (blind shift closing, setting-gated) — everyone except plain Cajero. */
export const canDoShiftClosing = (role?: string): boolean => !isCashier(role);

/**
 * Roles de piso (Mesero, Cajero, Cajero/Mesero, Chef): operan 2-5 pantallas
 * todo el turno, casi siempre en tablet o celular. MainLayout les monta el
 * "modo piso" (dock en el header / tab bar inferior) en vez del sidebar de
 * gestión. Es SOLO chrome: las rutas y los gates de permisos no dependen de
 * esto — el menú que alimenta el dock ya viene filtrado por rol.
 */
export const isFloorStaff = (role?: string): boolean =>
  isWaiter(role) || isCashier(role) || isCashierWaiter(role) || isChef(role);

/**
 * Check if user is a support user (email ends with @massresto.com)
 */
export const isSupportUser = (email?: string): boolean => {
  if (!email) return false;
  return email.toLowerCase().endsWith('@massresto.com');
};

/**
 * Get the default route for a user based on their role
 */
export const getDefaultRouteForRole = (role?: string): string => {
  if (!role) return '/dashboard';

  switch (role as UserRole) {
    case UserRole.MESERO:
      return '/tables';
    case UserRole.CAJERO:
      return '/cash-register';
    case UserRole.CAJERO_MESERO:
      return '/tables'; // Cajero/Mesero inicia en mesas (puede comandear Y cobrar)
    case UserRole.CHEF:
      return '/kitchen'; // Chefs go directly to kitchen display
    case UserRole.SUPER_ADMIN:
    case UserRole.ADMINISTRADOR:
    case UserRole.GERENTE:
    default:
      return '/dashboard';
  }
};
